import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  CalendarToday,
  Repeat,
  Schedule,
  LockOutlined,
} from '@mui/icons-material';
import {
  formatHours,
  formatOperationalDays,
  formatFullAddress,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  getAmenityIcon,
  PlanFeature,
  Reveal,
  AnimatedNumber,
  MotionList,
  MotionItem,
} from './themeHelpers';

const EDITORIAL_BG = '#FAFAF8';
const EDITORIAL_PAPER = '#F5F4EF';
const EDITORIAL_DARK = '#1A1A1A';

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function EditorialTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const galleryImages = images.slice(0, 6);

  const defaultDescription = `${library.name} is more than a library — it is a space where ambition meets quiet. Designed for the serious student, every corner invites deep work and inspired thinking.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);
  const fullAddress = formatFullAddress(library);

  const availSeats = library.available_seats ?? library.capacity;
  const seatDisplay =
    availSeats !== null && availSeats !== undefined && library.capacity
      ? `${availSeats} / ${library.capacity}`
      : library.capacity
      ? String(library.capacity)
      : null;

  const openLightbox = (idx) => { setLightboxIdx(idx); setLightboxOpen(true); };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: EDITORIAL_BG, color: EDITORIAL_DARK }}>

      {/* ── HERO — full-bleed photograph, title overlaid ── */}
      <Box sx={{ position: 'relative', height: { xs: '60vh', md: '85vh' }, overflow: 'hidden' }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {/* Dark gradient for readability */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0.72) 100%)',
        }} />

        {/* Arrows */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              aria-label="Previous image"
              sx={{
                position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)', color: EDITORIAL_DARK,
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              aria-label="Next image"
              sx={{
                position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)', color: EDITORIAL_DARK,
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        {/* Dots */}
        {images.length > 1 && (
          <Box sx={{ position: 'absolute', bottom: 100, left: 40, display: 'flex', gap: 1 }}>
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                sx={{
                  width: 32, height: 2,
                  bgcolor: idx === currentImageIndex ? 'white' : 'rgba(255,255,255,0.35)',
                  cursor: 'pointer', transition: 'all 0.3s',
                }}
              />
            ))}
          </Box>
        )}

        {/* Hero typography — oversized editorial */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 6 } }}>
          <Container maxWidth="lg">
            <Reveal y={28} duration={0.65}>
              {/* Issue-style label */}
              <Typography
                variant="overline"
                sx={{ color: accentColor, letterSpacing: 5, fontWeight: 700, fontSize: '0.65rem', display: 'block', mb: 1.5 }}
              >
                Study Space — {library.city || 'India'}
              </Typography>

              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Georgia", "Times New Roman", serif',
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '5.5rem', lg: '7rem' },
                  lineHeight: { xs: 1.1, md: 0.95 },
                  letterSpacing: { xs: '-0.5px', md: '-2px' },
                  color: 'white',
                  mb: 3,
                  // Limit width so it wraps deliberately
                  maxWidth: { md: '75%' },
                }}
              >
                {library.name}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.75)' }}>
                  <LocationOn fontSize="small" />
                  <Typography variant="body2">{library.address}</Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => onBookNow()}
                  sx={{
                    bgcolor: accentColor,
                    px: 3, py: 1.25,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
                  }}
                >
                  Book a Seat
                </Button>
              </Box>
            </Reveal>
          </Container>
        </Box>
      </Box>

      {/* ── EDITORIAL BODY — asymmetric 2-column grid ── */}
      <Box sx={{ bgcolor: EDITORIAL_BG, borderBottom: '1px solid #e8e5e0' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 12 } }}>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">

            {/* Left column: big pull quote + about */}
            <Grid item xs={12} md={7}>
              <Reveal>
                {/* Accent rule */}
                <Box sx={{ width: 64, height: 4, bgcolor: accentColor, mb: 4 }} />

                {/* Pull quote — uses library.description opening */}
                <Box sx={{
                  borderLeft: `4px solid ${accentColor}`,
                  pl: 3, mb: 4,
                  py: 1,
                }}>
                  <Typography
                    sx={{
                      fontFamily: '"Georgia", serif',
                      fontSize: { xs: '1.3rem', md: '1.65rem' },
                      fontStyle: 'italic',
                      lineHeight: 1.5,
                      color: EDITORIAL_DARK,
                    }}
                  >
                    "{library.description
                      ? library.description.split('.')[0] + '.'
                      : `A sanctuary for serious study in ${library.city || 'the city'}.`}"
                  </Typography>
                </Box>

                {/* Drop-cap about paragraph */}
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.9,
                    color: '#444',
                    fontSize: '1.05rem',
                    '&::first-letter': {
                      fontSize: '3.5em',
                      fontFamily: '"Georgia", serif',
                      fontWeight: 700,
                      float: 'left',
                      lineHeight: 0.8,
                      mr: 1,
                      mt: 0.5,
                      color: accentColor,
                    },
                  }}
                >
                  {library.description || defaultDescription}
                </Typography>
              </Reveal>
            </Grid>

            {/* Right column: sidebar facts */}
            <Grid item xs={12} md={5}>
              <Reveal delay={0.1}>
                <Box
                  sx={{
                    bgcolor: EDITORIAL_PAPER,
                    border: '1px solid #e8e5e0',
                    p: { xs: 3, md: 4 },
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: accentColor, letterSpacing: 3, fontWeight: 700, fontSize: '0.65rem', display: 'block', mb: 3 }}
                  >
                    At a Glance
                  </Typography>

                  {seatDisplay && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h2"
                        sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: EDITORIAL_DARK, lineHeight: 1 }}
                      >
                        <AnimatedNumber value={seatDisplay} />
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888', letterSpacing: 1, textTransform: 'uppercase' }}>
                        Seats Available
                      </Typography>
                      <Box sx={{ mt: 2, mb: 3, height: '1px', bgcolor: '#e8e5e0' }} />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <AccessTime sx={{ color: accentColor, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600} color={EDITORIAL_DARK}>
                        {hoursStr || 'Contact for hours'}
                      </Typography>
                      {daysStr && (
                        <Typography variant="caption" color="text.secondary">{daysStr}</Typography>
                      )}
                    </Box>
                  </Box>

                  {library.locker_count > 0 && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <LockOutlined sx={{ color: accentColor, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body2" color="#555">{library.locker_count} secure lockers</Typography>
                    </Box>
                  )}
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── PLANS — editorial card grid ── */}
      <Box sx={{ bgcolor: EDITORIAL_PAPER, borderBottom: '1px solid #e8e5e0' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
          <Reveal>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, mb: 5, flexWrap: 'wrap' }}>
              <Typography
                variant="h3"
                sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: EDITORIAL_DARK }}
              >
                Membership
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#d8d5d0', minWidth: 40, mt: 'auto', mb: '6px' }} />
            </Box>
          </Reveal>

          {plans.length > 0 ? (
            <Grid container spacing={3}>
              {plans.map((plan, idx) => {
                const planName = plan.name || titleCasePlanType(plan.plan_type);
                const price = formatPrice(plan.price_cents);
                const period = planPeriodLabel(plan.plan_type);
                const discPct = plan.first_time_discount;
                const discPrice = discountedFirstPrice(plan.price_cents, discPct);

                return (
                  <Grid item xs={12} sm={6} md={3} key={plan.id}>
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: EDITORIAL_BG,
                        border: '1px solid #ddd',
                        borderTop: `3px solid ${idx === 0 ? accentColor : '#ddd'}`,
                        borderRadius: 0,
                        height: '100%',
                        position: 'relative',
                        transition: 'border-top-color 0.25s, transform 0.25s',
                        '&:hover': {
                          borderTopColor: accentColor,
                          transform: 'translateY(-3px)',
                        },
                      }}
                    >
                      {discPct > 0 && (
                        <Chip
                          label={`${discPct}% OFF first time`}
                          size="small"
                          sx={{
                            position: 'absolute', top: 12, right: 12,
                            bgcolor: accentColor, color: 'white', fontWeight: 700, fontSize: '0.65rem', borderRadius: 0,
                          }}
                        />
                      )}

                      <CardContent sx={{ p: 3 }}>
                        <Typography
                          variant="overline"
                          sx={{ color: '#999', letterSpacing: 2, display: 'block', mb: 2 }}
                        >
                          {planName}
                        </Typography>

                        {price && (
                          <>
                            {discPct > 0 && discPrice !== null ? (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: '#aaa', textDecoration: 'line-through' }}>{price}</Typography>
                                <Typography variant="h4" fontWeight={700} sx={{ color: accentColor, fontFamily: '"Georgia", serif' }}>
                                  {formatPrice(discPrice)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">first time, then {price}</Typography>
                              </Box>
                            ) : (
                              <Typography variant="h3" fontWeight={700} sx={{ color: EDITORIAL_DARK, fontFamily: '"Georgia", serif', mb: 0.5 }}>
                                {price}
                              </Typography>
                            )}
                            {period && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                {period}
                              </Typography>
                            )}
                          </>
                        )}

                        <Box sx={{ my: 2, height: '1px', bgcolor: '#e8e5e0' }} />

                        <Box sx={{ mb: 2 }}>
                          {plan.duration_days && (
                            <PlanFeature icon={<CalendarToday />} color="#777">
                              Valid {plan.duration_days} days
                            </PlanFeature>
                          )}
                          <PlanFeature icon={<Schedule />} color="#777">
                            {hoursLabel(plan.hours_per_day)}
                          </PlanFeature>
                          {plan.recurring && (
                            <PlanFeature icon={<Repeat />} color={accentColor}>
                              Auto-renews
                            </PlanFeature>
                          )}
                        </Box>

                        {plan.description ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box', WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 2,
                              lineHeight: 1.5,
                            }}
                          >
                            {plan.description}
                          </Typography>
                        ) : null}

                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => onBookNow(plan)}
                          sx={{
                            bgcolor: EDITORIAL_DARK, borderRadius: 0,
                            '&:hover': { bgcolor: accentColor },
                            fontWeight: 700, letterSpacing: 1,
                            fontSize: '0.75rem',
                          }}
                        >
                          Select Plan
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box sx={{ bgcolor: EDITORIAL_BG, border: '1px solid #ddd', p: 5, textAlign: 'center' }}>
              <Typography variant="h6" color={EDITORIAL_DARK} gutterBottom>Contact us for membership details</Typography>
              <Button
                variant="contained"
                onClick={() => onBookNow()}
                sx={{ bgcolor: EDITORIAL_DARK, '&:hover': { bgcolor: accentColor }, borderRadius: 0, fontWeight: 700 }}
              >
                Get in Touch
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* ── GALLERY ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
        <Reveal>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, mb: 5, flexWrap: 'wrap' }}>
            <Typography variant="h3" sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: EDITORIAL_DARK }}>
              Gallery
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#d8d5d0', minWidth: 40, mt: 'auto', mb: '6px' }} />
          </Box>
        </Reveal>

        {/* Masonry-style: first image larger */}
        <Grid container spacing={1.5}>
          {galleryImages.map((img, idx) => (
            <Grid item xs={idx === 0 ? 12 : 6} md={idx === 0 ? 8 : 4} key={idx}
              sx={{ ...(idx === 0 && { md: { gridRow: 'span 2' } }) }}
            >
              <Box
                component="img"
                src={img}
                alt={`Gallery ${idx + 1}`}
                onClick={() => openLightbox(idx)}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: idx === 0 ? { xs: 220, md: 420 } : 200,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  transition: 'opacity 0.25s',
                  '&:hover': { opacity: 0.88 },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── AMENITIES ── */}
      {library.amenities?.length > 0 && (
        <Box sx={{ bgcolor: EDITORIAL_PAPER, borderTop: '1px solid #e8e5e0', borderBottom: '1px solid #e8e5e0' }}>
          <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
            <Reveal>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, mb: 5, flexWrap: 'wrap' }}>
                <Typography variant="h3" sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: EDITORIAL_DARK }}>
                  Amenities
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: '#d8d5d0', minWidth: 40, mt: 'auto', mb: '6px' }} />
              </Box>
            </Reveal>
            <MotionList style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              {library.amenities.map((amenity, idx) => (
                <MotionItem key={idx}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    py: 1.5, borderBottom: '1px solid #e8e5e0',
                  }}>
                    <Box sx={{ color: accentColor, display: 'flex' }}>
                      {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 18 } })}
                    </Box>
                    <Typography variant="body2" color={EDITORIAL_DARK}>{amenity}</Typography>
                  </Box>
                </MotionItem>
              ))}
            </MotionList>
          </Container>
        </Box>
      )}

      {/* ── LOCATION ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
        <Reveal>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, mb: 5, flexWrap: 'wrap' }}>
            <Typography variant="h3" sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: EDITORIAL_DARK }}>
              Location
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#d8d5d0', minWidth: 40, mt: 'auto', mb: '6px' }} />
          </Box>
        </Reveal>
        <Grid container spacing={5}>
          <Grid item xs={12} md={7}>
            <Box sx={{ height: 320, bgcolor: '#f0ede8', border: '1px solid #ddd', overflow: 'hidden' }}>
              {library.lat && library.lng ? (
                <iframe
                  title="Library Location"
                  width="100%" height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                />
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">Map unavailable</Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {fullAddress && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <LocationOn sx={{ color: accentColor, mt: 0.3, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} color={EDITORIAL_DARK}>Address</Typography>
                    <Typography variant="body2" color="text.secondary">{fullAddress}</Typography>
                  </Box>
                </Box>
              )}
              {library.contact_phone && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Phone sx={{ color: accentColor, mt: 0.3, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} color={EDITORIAL_DARK}>Phone</Typography>
                    <Typography variant="body2" color="text.secondary">
                      <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {library.contact_phone}
                      </a>
                    </Typography>
                  </Box>
                </Box>
              )}
              {library.contact_email && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Email sx={{ color: accentColor, mt: 0.3, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} color={EDITORIAL_DARK}>Email</Typography>
                    <Typography variant="body2" color="text.secondary">
                      <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {library.contact_email}
                      </a>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* ── CTA — big editorial spread ── */}
      <Box
        sx={{
          bgcolor: EDITORIAL_DARK, py: { xs: 10, md: 14 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative rule */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: accentColor }} />
        <Container maxWidth="md">
          <Reveal>
            <Typography
              sx={{
                fontFamily: '"Georgia", serif',
                fontWeight: 700,
                fontSize: { xs: '2.2rem', md: '4rem' },
                color: 'white',
                lineHeight: 1.1,
                mb: 3,
              }}
            >
              Start your best chapter at {library.name}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', mb: 5, fontSize: '1.1rem' }}>
              Claim your seat and write your story in the quiet.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => onBookNow()}
              sx={{
                bgcolor: accentColor, px: 5, py: 1.5, borderRadius: 0,
                fontWeight: 700, letterSpacing: 1.5, fontSize: '0.85rem',
                '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
              }}
            >
              Book Now
            </Button>
          </Reveal>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ bgcolor: EDITORIAL_DARK, borderTop: '1px solid rgba(255,255,255,0.08)', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', fontFamily: '"Georgia", serif' }}>
              {library.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              Powered by{' '}
              <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor }}>
                BookMyLibrary
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Lightbox ── */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#111', borderRadius: 0 } }}
      >
        <IconButton
          onClick={() => setLightboxOpen(false)}
          aria-label="Close gallery"
          sx={{ position: 'absolute', right: 8, top: 8, color: 'rgba(255,255,255,0.7)', zIndex: 1 }}
        >
          <Close />
        </IconButton>
        <Box
          component="img"
          src={galleryImages[lightboxIdx]}
          alt="Gallery"
          loading="lazy"
          decoding="async"
          sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Dialog>
    </Box>
  );
}

export default EditorialTheme;
